import { useEffect, useMemo, useRef } from 'react';
import { Platform } from 'react-native';
import { Gesture } from 'react-native-gesture-handler';
import {
  cancelAnimation,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import {
  MAX_SCALE,
  MIN_SCALE,
  PAN_AXIS_LOCK_THRESHOLD,
  RUBBER_BAND_FACTOR,
  SCALE_EPSILON,
  SPRING_CONFIG,
  SWIPE_CLOSE_VELOCITY,
  SWIPE_COMMIT_RATIO,
  SWIPE_COMMIT_VELOCITY,
  TRACK_SNAP_TIMING,
} from './ImageViewerConstants';
import { checkTapInsideMorph, performZoomToggle } from './ImageViewerHelpers';
import type { IImageViewerSharedValues } from './useImageViewerSharedValues';
import type { IPaneSlots } from './usePaneSlots';

interface UseImageViewerGesturesProps {
  sv: IImageViewerSharedValues;
  paneSlots: IPaneSlots;
  isDesktop: boolean;
  paneWidth: number;
  closeViewer: () => void;
  closeViewerInstant: () => void;
  toggleUiVisibility: () => void;
  // Carousel (always enabled when there are >1 photos in the viewer).
  isCarouselEnabled?: boolean;
  // Current active index and total photo count, surfaced so the worklet can
  // apply rubber-band damping at the carousel edges and compute snap targets.
  activeIndex: number;
  photoCount: number;
  // Commits a slot shift after the snap animation completes. direction is -1
  // for prev, +1 for next. The carousel atomically rotates one slot's
  // pageIndex (and bumps activeIndex / activeSlot) to preserve the visible
  // center pixel across the commit.
  onCommitShift: (direction: -1 | 1) => void;
  // Disable gestures during navigation-triggered close to prevent race conditions
  gesturesEnabled?: boolean;
}

export const useImageViewerGestures = ({
  sv,
  paneSlots,
  isDesktop,
  paneWidth,
  closeViewer,
  closeViewerInstant,
  toggleUiVisibility,
  isCarouselEnabled,
  activeIndex,
  photoCount,
  onCommitShift,
  gesturesEnabled = true,
}: UseImageViewerGesturesProps) => {
  // Stable callback refs (avoid gesture tree rebuild on every callback identity change).
  const closeViewerRef = useRef(closeViewer);
  const closeViewerInstantRef = useRef(closeViewerInstant);
  const toggleUiVisibilityRef = useRef(toggleUiVisibility);
  const onCommitShiftRef = useRef(onCommitShift);
  const gesturesEnabledRef = useRef(gesturesEnabled);

  // Worklet-readable flags / counters. Updated from JS via useEffect; reading
  // them from worklets avoids re-creating the gesture tree on every change.
  const gesturesEnabledShared = useSharedValue(gesturesEnabled);
  const isCarouselEnabledShared = useSharedValue(!!isCarouselEnabled);
  const activeIndexShared = useSharedValue(activeIndex);
  const photoCountShared = useSharedValue(photoCount);
  const paneWidthShared = useSharedValue(paneWidth);

  // Pan axis lock for non-zoomed pans: 0 = undetermined, 1 = horizontal (track),
  // 2 = vertical (pull-to-close on active pane).
  const panAxis = useSharedValue<0 | 1 | 2>(0);

  useEffect(() => {
    closeViewerRef.current = closeViewer;
    closeViewerInstantRef.current = closeViewerInstant;
    toggleUiVisibilityRef.current = toggleUiVisibility;
    onCommitShiftRef.current = onCommitShift;
    gesturesEnabledRef.current = gesturesEnabled;
    gesturesEnabledShared.value = gesturesEnabled;
    isCarouselEnabledShared.value = !!isCarouselEnabled;
    activeIndexShared.value = activeIndex;
    photoCountShared.value = photoCount;
    paneWidthShared.value = paneWidth;
  }, [
    closeViewer,
    closeViewerInstant,
    toggleUiVisibility,
    onCommitShift,
    gesturesEnabled,
    isCarouselEnabled,
    activeIndex,
    photoCount,
    paneWidth,
  ]);

  // Stable JS callbacks that respect the gesturesEnabled flag.
  const callCloseViewer = () => {
    if (!gesturesEnabledRef.current) return;
    closeViewerRef.current?.();
  };
  const callCloseViewerInstant = () => {
    if (!gesturesEnabledRef.current) return;
    closeViewerInstantRef.current?.();
  };
  const callToggleUiVisibility = () => {
    if (!gesturesEnabledRef.current) return;
    toggleUiVisibilityRef.current?.();
  };
  const callCommitShift = (direction: -1 | 1) => {
    if (!gesturesEnabledRef.current) return;
    onCommitShiftRef.current?.(direction);
  };

  // Gesture tree rebuilt only when isDesktop changes. All other dynamic state
  // is read via shared values inside worklets to keep updateHandlers cheap.
  const composedGesture = useMemo(() => {
    // Worklet-side helper: returns the slot bag currently considered active.
    // The carousel keeps paneSlots.activeSlot in sync after each navigation
    // commit, so reading it here always resolves to the centered pane.
    const getActivePane = () => {
      'worklet';
      return paneSlots.slots[paneSlots.activeSlot.value];
    };

    // Pinch always targets the currently active pane.
    const pinchGesture = Gesture.Pinch()
      .onStart(() => {
        'worklet';
        if (!gesturesEnabledShared.value) return;
        const active = getActivePane();
        sv.isGestureActive.value = true;
        active.baseScale.value = active.scale.value;
      })
      .onUpdate((event) => {
        'worklet';
        if (!gesturesEnabledShared.value) return;
        const active = getActivePane();
        active.scale.value = active.baseScale.value * event.scale;
      })
      .onEnd(() => {
        'worklet';
        sv.isGestureActive.value = false;
        if (!gesturesEnabledShared.value) return;
        const active = getActivePane();

        let finalScale = active.scale.value;
        if (finalScale < MIN_SCALE) finalScale = MIN_SCALE;
        if (finalScale > MAX_SCALE) finalScale = MAX_SCALE;

        if (finalScale !== active.scale.value) {
          active.scale.value = withSpring(finalScale, SPRING_CONFIG);
        }
        active.lastScale.value = finalScale;
      })
      .shouldCancelWhenOutside(true);

    const panGesture = Gesture.Pan()
      .minPointers(1)
      .maxPointers(1)
      .onStart(() => {
        'worklet';
        if (!gesturesEnabledShared.value) return;
        sv.isGestureActive.value = true;
        panAxis.value = 0;
        // Snapshot the resting trackTranslateX so a pan started mid-snap
        // continues smoothly from the current intermediate position.
        cancelAnimation(sv.trackTranslateX);
        sv.lastTrackTranslateX.value = sv.trackTranslateX.value;
        if (Platform.OS === 'web') {
          sv.isPanning.value = true;
        }
      })
      .onUpdate((event) => {
        'worklet';
        if (!gesturesEnabledShared.value) return;

        const active = getActivePane();
        const isZoomed = active.scale.value > 1 + SCALE_EPSILON;

        // Zoomed: free 2D in-pane pan. Translation is in pre-scale image space.
        if (isZoomed) {
          const dx = event.translationX / active.scale.value;
          const dy = event.translationY / active.scale.value;
          active.translateX.value = active.lastTranslateX.value + dx;
          active.translateY.value = active.lastTranslateY.value + dy;
          return;
        }

        // Non-zoomed: lock to the dominant axis once movement crosses the threshold.
        if (panAxis.value === 0) {
          const absX = Math.abs(event.translationX);
          const absY = Math.abs(event.translationY);
          if (absX >= PAN_AXIS_LOCK_THRESHOLD || absY >= PAN_AXIS_LOCK_THRESHOLD) {
            panAxis.value = absX >= absY ? 1 : 2;
          }
        }

        if (panAxis.value === 1) {
          // Horizontal: drive the carousel track. Apply rubber-band damping at
          // edges. The "rest" position is -activeIndex * paneWidth; the user's
          // drag offset relative to rest is (raw - rest).
          const width = paneWidthShared.value;
          const rest = -activeIndexShared.value * width;
          const raw = sv.lastTrackTranslateX.value + event.translationX;
          let dragOffset = raw - rest;
          if (isCarouselEnabledShared.value) {
            const atFirst = activeIndexShared.value <= 0;
            const atLast = activeIndexShared.value >= photoCountShared.value - 1;
            if (atFirst && dragOffset > 0) {
              dragOffset = dragOffset * RUBBER_BAND_FACTOR;
            } else if (atLast && dragOffset < 0) {
              dragOffset = dragOffset * RUBBER_BAND_FACTOR;
            }
          } else {
            // No carousel: still allow horizontal drag with full damping (visual feedback only).
            dragOffset = dragOffset * RUBBER_BAND_FACTOR;
          }
          sv.trackTranslateX.value = rest + dragOffset;
        } else if (panAxis.value === 2) {
          // Vertical: pull-to-close — translate the active pane.
          active.translateX.value = active.lastTranslateX.value;
          active.translateY.value = active.lastTranslateY.value + event.translationY;
        }
      })
      .onEnd((event) => {
        'worklet';
        sv.isGestureActive.value = false;
        if (Platform.OS === 'web') {
          sv.isPanning.value = false;
        }
        if (!gesturesEnabledShared.value) return;

        const active = getActivePane();
        const isZoomed = active.scale.value > 1 + SCALE_EPSILON;

        if (isZoomed) {
          // Commit zoomed pan accumulators.
          const dx = event.translationX / active.scale.value;
          const dy = event.translationY / active.scale.value;
          active.lastTranslateX.value = active.lastTranslateX.value + dx;
          active.lastTranslateY.value = active.lastTranslateY.value + dy;
          active.translateX.value = active.lastTranslateX.value;
          active.translateY.value = active.lastTranslateY.value;
          return;
        }

        // Non-zoomed: pull-to-close on vertical drag.
        if (panAxis.value === 2) {
          const shouldClose =
            (event.velocityY > SWIPE_CLOSE_VELOCITY && event.translationY > 60) ||
            event.translationY > 120;
          if (shouldClose) {
            scheduleOnRN(callCloseViewerInstant);
            return;
          }
          // Spring back to center.
          active.lastTranslateX.value = 0;
          active.lastTranslateY.value = 0;
          active.translateX.value = withSpring(0, SPRING_CONFIG);
          active.translateY.value = withSpring(0, SPRING_CONFIG);
          return;
        }

        // Non-zoomed horizontal: track snap or commit.
        if (panAxis.value === 1) {
          const width = paneWidthShared.value;
          const rest = -activeIndexShared.value * width;
          const dragOffset = sv.trackTranslateX.value - rest;
          const atFirst = activeIndexShared.value <= 0;
          const atLast = activeIndexShared.value >= photoCountShared.value - 1;

          // Decide commit direction.
          let commitDir: -1 | 0 | 1 = 0;
          if (isCarouselEnabledShared.value) {
            const passedThreshold = Math.abs(dragOffset) > width * SWIPE_COMMIT_RATIO;
            const fastEnough = Math.abs(event.velocityX) > SWIPE_COMMIT_VELOCITY;
            if ((passedThreshold || fastEnough) && (dragOffset !== 0)) {
              if (dragOffset < 0 && !atLast) {
                commitDir = 1;
              } else if (dragOffset > 0 && !atFirst) {
                commitDir = -1;
              }
            }
          }

          if (commitDir !== 0) {
            // Snap to the neighbor's resting position. After the snap,
            // trackTranslateX = -(activeIndex + commitDir) * paneWidth — i.e.,
            // already at the resting position for the new active index. The
            // commit step only updates JS state (activeIndex, slotPageIndices,
            // activeSlot) and does NOT touch trackTranslateX, so the visible
            // center pixel does not move across the commit.
            const target = -(activeIndexShared.value + commitDir) * width;
            sv.trackTranslateX.value = withTiming(target, TRACK_SNAP_TIMING, (finished) => {
              'worklet';
              if (finished) {
                scheduleOnRN(callCommitShift, commitDir as -1 | 1);
              }
            });
          } else {
            // Spring back to rest at the current active index.
            sv.trackTranslateX.value = withSpring(rest, SPRING_CONFIG);
          }
          return;
        }

        // No axis locked (very small movement): nothing to do.
      })
      .shouldCancelWhenOutside(true);

    // Double-tap zoom on the active pane.
    const doubleTapGesture = Gesture.Tap()
      .numberOfTaps(2)
      .onEnd(() => {
        'worklet';
        if (!gesturesEnabledShared.value) return;
        performZoomToggle(getActivePane());
      });

    // Web (mobile) tap: toggle UI inside, close outside.
    const tapGesture = Gesture.Tap()
      .onEnd((event, success) => {
        'worklet';
        if (!success || !gesturesEnabledShared.value) return;
        const isInside = checkTapInsideMorph(event, sv, getActivePane());
        if (isInside) {
          scheduleOnRN(callToggleUiVisibility);
        } else {
          scheduleOnRN(callCloseViewer);
        }
      });

    // Desktop web tap: zoom on inside click, close on outside.
    const largeScreenWebTapGesture = Gesture.Tap()
      .onEnd((event, success) => {
        'worklet';
        if (!success || !gesturesEnabledShared.value) return;
        const active = getActivePane();
        const isInside = checkTapInsideMorph(event, sv, active);
        if (isInside) {
          performZoomToggle(active);
        } else {
          scheduleOnRN(callCloseViewer);
        }
      });

    // Native single tap: toggle UI inside; close outside (when not zoomed).
    const singleTapToClose = Gesture.Tap()
      .onEnd((event, success) => {
        'worklet';
        if (!success || !gesturesEnabledShared.value) return;
        const active = getActivePane();
        const isInside = checkTapInsideMorph(event, sv, active);
        if (isInside) {
          scheduleOnRN(callToggleUiVisibility);
        } else if (active.scale.value <= 1.05) {
          scheduleOnRN(callCloseViewer);
        }
      });

    return Platform.select({
      web: isDesktop
        ? Gesture.Race(Gesture.Simultaneous(pinchGesture, panGesture), largeScreenWebTapGesture)
        : Gesture.Race(
            Gesture.Simultaneous(pinchGesture, panGesture),
            Gesture.Exclusive(doubleTapGesture, tapGesture)
          ),
      default: Gesture.Race(
        Gesture.Simultaneous(pinchGesture, panGesture),
        Gesture.Exclusive(doubleTapGesture, singleTapToClose)
      ),
    });
  }, [isDesktop]);

  return composedGesture;
};
